/**
 * Итог-попап «Подтвердить всех» (задача 116 §2.2, макап 3b).
 *
 * Показывается после `approve-week-bulk`: блок успеха (кто подтверждён, с часами)
 * + блок «Не подтверждены · нужно исправить» (кто не прошёл и почему — пустые
 * типизированные причины из `blocking_reasons`). Generic `<Dialog>` (`patterns.md §Dialog`);
 * данные — `WeekBulkApproveResult` (§1.3). Сами исправления делаются в ленте.
 */
import { useTranslation } from "react-i18next";
import { addDays, format, parseISO } from "date-fns";
import { ru as ruLocale, enUS, type Locale } from "date-fns/locale";
import { Check } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatHoursHalf } from "@/lib/timesheet";
import type { WeekApproveOutcome, WeekBulkApproveResult } from "@/api/approval";

interface WeekApproveSummaryDialogProps {
  result: WeekBulkApproveResult | null;
  onClose: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

export function WeekApproveSummaryDialog({
  result,
  onClose,
}: WeekApproveSummaryDialogProps): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? enUS : ruLocale;
  if (!result) return null;

  const from = parseISO(result.week_start);
  const to = addDays(from, 6);
  const rangeLabel = `${format(from, "dd.MM", { locale })} – ${format(to, "dd.MM", { locale })}`;

  const confirmed = result.outcomes.filter((o) => o.confirmation_state === "confirmed");
  const rejected = result.outcomes.filter((o) => o.confirmation_state === "blocked");
  const notRequired = result.outcomes.filter((o) => o.confirmation_state === "not_required");
  const confirmedHours = confirmed.reduce((sum, o) => sum + o.week_declared_hours_half, 0);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-h-[calc(100dvh-var(--sp-8))] grid-rows-[auto_minmax(0,1fr)_auto] sm:max-w-xl"
        data-testid="week-summary-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{t("week.summary.title", { range: rangeLabel })}</DialogTitle>
        </DialogHeader>

        <div
          className="flex min-h-0 flex-col gap-4 overflow-y-auto"
          data-testid="week-summary-results"
        >
          {confirmed.length > 0 && (
            <div
              className="tone-success flex items-start gap-2 rounded-[var(--radius-card)] px-3 py-2.5 text-sm"
              data-testid="week-summary-confirmed"
            >
              <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0">
                <b>
                  {t("week.summary.confirmed", {
                    count: confirmed.length,
                    hours: formatHoursHalf(confirmedHours),
                  })}
                </b>{" "}
                {confirmed.map((o) => o.user.display_name).join(", ")}
              </span>
            </div>
          )}

          {rejected.length > 0 && (
            <div className="flex flex-col gap-2" data-testid="week-summary-rejected">
              <span
                className="font-condensed text-xs font-semibold uppercase tracking-[0.06em]"
                style={{ color: "var(--tone-danger-fg)" }}
              >
                {t("week.summary.rejectedTitle")}
              </span>
              {rejected.map((o) => (
                <RejectedRow key={o.user.id} outcome={o} locale={locale} />
              ))}
            </div>
          )}

          {notRequired.length > 0 && (
            <div
              className="tone-neutral flex items-start gap-2 rounded-[var(--radius-card)] px-3 py-2.5 text-sm"
              data-testid="week-summary-not-required"
            >
              <span className="min-w-0">
                <b>{t("week.summary.notRequiredTitle", { count: notRequired.length })}</b>{" "}
                {notRequired.map((o) => o.user.display_name).join(", ")}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <DialogClose asChild>
            <Button size="default" data-testid="week-summary-close">
              {t("week.summary.ok")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectedRow({
  outcome,
  locale,
}: {
  outcome: WeekApproveOutcome;
  locale: Locale;
}): React.ReactElement {
  const { t } = useTranslation();
  const problems = outcome.blocking_reasons ?? [];
  return (
    <div
      data-testid={`week-summary-rejected-${outcome.user.id}`}
      className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)] p-2.5"
      style={{
        borderColor: "var(--rule-line)",
        borderLeftWidth: "3px",
        borderLeftColor: "var(--alert-red)",
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-chip)] font-mono text-xs font-semibold uppercase"
          style={{ background: "var(--bg-panel-1)", color: "var(--fg-2)" }}
          aria-hidden="true"
        >
          {initials(outcome.user.display_name)}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--fg-1)]">
          {outcome.user.display_name}
        </span>
        <span
          className="shrink-0 font-mono text-sm tabular-nums"
          style={{ color: "var(--tone-danger-fg)" }}
        >
          {formatHoursHalf(outcome.week_declared_hours_half, "")} /{" "}
          {formatHoursHalf(outcome.week_norm_hours_half)}
        </span>
      </div>
      <ul className="flex flex-col gap-1 pl-1">
        {problems.map((p) => (
          <li key={p.date} className="flex items-baseline gap-1.5 text-xs text-[var(--fg-2)]">
            <span
              aria-hidden="true"
              className="mt-1 size-1.5 shrink-0 rounded-full"
              style={{ background: "var(--tone-danger-fg)" }}
            />
            <span>
              {t(p.kind === "draft" ? "week.summary.draftReport" : "week.summary.noReport", {
                date: format(parseISO(p.date), "EEEEEE dd.MM", { locale }),
              })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
